/**
 * HostIntakeFieldsTab (Iter 9.40 - Sync Architecture)
 *
 * Lets a host toggle WHICH admin-canonical preference fields they want to ask
 * their clients at RSVP time. Free-tier categories (events, instructors) can
 * only toggle cross-category fields (allergies, accessibility, dietary).
 *
 * Usage (host dashboard):
 *   <HostIntakeFieldsTab eventId={event.event_id} />
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, Info, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HostIntakeFieldsTab({ eventId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(new Set());

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    const token = localStorage.getItem("session_token");
    fetch(`${API}/host/events/${eventId}/intake-fields`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        setData(d);
        setEnabled(new Set(d?.enabled_field_keys || []));
      })
      .finally(() => setLoading(false));
  }, [eventId]);

  const toggle = (key) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    const token = localStorage.getItem("session_token");
    setSaving(true);
    try {
      const r = await fetch(`${API}/host/events/${eventId}/intake-fields`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled_field_keys: Array.from(enabled) }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Intake updated · ${d.enabled_field_keys.length} fields enabled`);
        if (d.rejected?.length) {
          toast.info(`${d.rejected.length} fields blocked by admin rules`);
        }
      } else {
        toast.error("Could not save");
      }
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <Card><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></CardContent></Card>;
  }
  if (!data) {
    return <Card><CardContent className="p-6 text-sm text-gray-500">Could not load intake config</CardContent></Card>;
  }

  const totalFields = (data.canonical_blocks || []).reduce((n, b) => n + (b.fields?.length || 0), 0);

  return (
    <Card data-testid="host-intake-fields-tab">
      <CardContent className="p-5 sm:p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Intake questions</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              Toggle which preference questions you want to ask your clients at RSVP.
              Category-primary preferences are controlled by admin; you pick which
              specific options you care about. Allergies, accessibility and dietary
              are always available.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant="outline" className="text-[10px]">{enabled.size} / {totalFields} enabled</Badge>
            {data.is_free_tier && (
              <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] flex items-center gap-1">
                <Lock className="w-3 h-3" /> Free tier
              </Badge>
            )}
          </div>
        </div>

        {data.is_free_tier && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              Free-tier categories (events, instructors) can only ask cross-category
              questions (allergies, accessibility, dietary). Upgrade to a paid tier to
              unlock category-specific preferences.
            </p>
          </div>
        )}

        {(data.canonical_blocks || []).map((block) => (
          <div key={block.block_key} data-testid={`intake-block-${block.block_key}`}>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
              {block.block_label}
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {(block.fields || []).map((f) => {
                const isOn = enabled.has(f.key);
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => toggle(f.key)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border capitalize transition-all ${isOn ? "bg-black text-white border-black" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"}`}
                    data-testid={`intake-field-${f.key}`}
                    title={f.label}
                  >
                    {f.label || f.key.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <Button
          onClick={save}
          disabled={saving}
          className="w-full sm:w-auto h-10 bg-black hover:bg-gray-800"
          data-testid="host-intake-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Save intake config
        </Button>
      </CardContent>
    </Card>
  );
}
